import { useState, useEffect } from 'react';
import axios from 'axios';
import { PhoneIncoming, PhoneOutgoing, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
    if (!iso) return '-';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
            + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch { return '-'; }
}

function formatDuration(s) {
    s = parseInt(s || 0);
    if (!s) return '0s';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}min ${sec}s` : `${sec}s`;
}

export default function CallHistory({ prospectId, clientId, phone, title = "Historique des appels" }) {
    const [cdrs, setCdrs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = {};
        if (prospectId) params.prospect_id = prospectId;
        if (clientId) params.client_id = clientId;
        if (phone && !prospectId && !clientId) params.phone = phone;
        axios.get(`${API_URL}/api/ovh/cdrs`, { params, withCredentials: true })
            .then(r => setCdrs(r.data || []))
            .catch(() => setCdrs([]))
            .finally(() => setLoading(false));
    }, [prospectId, clientId, phone]);

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="call-history-section">
            {title !== '' && (
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#C9A962]" />
                    <h3 className="font-semibold text-[#1B365D]">{title}</h3>
                    <span className="ml-auto text-xs text-gray-400">{cdrs.length} appel{cdrs.length > 1 ? 's' : ''}</span>
                </div>
            )}
            <div className="p-4">
                {loading ? (
                    <div className="flex items-center justify-center py-6">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1B365D]" />
                    </div>
                ) : cdrs.length === 0 ? (
                    <div className="text-center py-6">
                        <Phone className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Aucun appel enregistré</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-50">
                        {cdrs.map((c) => {
                            const isIn = c.direction === 'inbound' || c.direction === 'incoming';
                            const number = isIn ? c.calling_number : c.called_number;
                            return (
                                <li key={c.id} className="flex items-center gap-3 py-2.5">
                                    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${isIn ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                                        {isIn ? <PhoneIncoming className="w-4 h-4" /> : <PhoneOutgoing className="w-4 h-4" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${isIn ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {isIn ? 'Entrant' : 'Sortant'}
                                            </span>
                                            <span className="text-sm text-gray-800 font-medium truncate">{number || '-'}</span>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-0.5">{formatDate(c.start_datetime)} • durée {formatDuration(c.duration_seconds)}</p>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
